import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, TextField, Button, Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Store, Package, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const PLATFORMS = [
  { name: 'Shopify', color: '#10b981', bg: 'rgba(16,185,129,0.06)', icon: Package },
  { name: 'Amazon', color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', icon: ShoppingCart },
  { name: 'Walmart', color: '#2383e2', bg: 'rgba(35,131,226,0.06)', icon: Store },
];

const CreateMasterPO = () => {
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  const getDefaultPOName = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    return `MHC${mm}${dd}${yy}`;
  };

  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [poName, setPoName] = useState(getDefaultPOName());

  const handleTogglePlatform = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  useEffect(() => {
    const fetchDefaultName = async () => {
      const base = getDefaultPOName();
      try {
        const { data } = await axios.get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/master-pos');
        const count = data.filter(mp => mp.poName.startsWith(base)).length;
        setPoName(`${base}-${count + 1}`);
      } catch { setPoName(`${base}-1`); }
    };
    fetchDefaultName();
  }, []);

  const handleCreateMasterPO = async () => {
    if (!poName || selectedPlatforms.length === 0) {
      setError('Please enter a PO name and select at least one platform.');
      return;
    }
    setIsCreating(true);
    setError(null);
    const t = toast.loading('Creating master purchase order…');
    try {
      const createdPurchaseOrders = await Promise.all(
        selectedPlatforms.map(async (platform) => {
          const response = await axios.post('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos', {
            poName: `${poName} - ${platform}`, platform, products: [],
          });
          return response.data._id;
        })
      );
      const masterPOResponse = await axios.post('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/master-pos', {
        poName, childPos: createdPurchaseOrders,
      });
      toast.success('Master purchase order created', { id: t });
      navigate(`/master-po/${masterPOResponse.data._id}`);
    } catch (error) {
      console.error('Error creating Master PO:', error);
      setError('Failed to create Master PO');
      toast.error('Failed to create Master PO', { id: t });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Box className="fade-in" sx={{ maxWidth: 520, mx: { xs: 0, sm: 'auto' } }}>
      <Button
        onClick={() => navigate('/master-pos')}
        startIcon={<ArrowLeft size={16} />}
        sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
      >
        Back
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
        New Purchase Order
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Create a master purchase order with platform-specific lists
      </Typography>

      {error && (
        <Box sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>{error}</Typography>
        </Box>
      )}

      <Stack spacing={3.5}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
            Purchase Order Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={poName}
            onChange={(e) => setPoName(e.target.value)}
            placeholder="e.g. MHC032126-1"
          />
        </Box>

        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
            Select Platforms
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {PLATFORMS.map(({ name, color, bg, icon: Icon }) => {
              const selected = selectedPlatforms.includes(name);
              return (
                <Box
                  key={name}
                  onClick={() => handleTogglePlatform(name)}
                  sx={{
                    flex: '1 1 140px',
                    p: 2,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: selected ? color : 'divider',
                    bgcolor: selected ? bg : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    position: 'relative',
                    '&:hover': {
                      borderColor: selected ? color : 'text.secondary',
                      bgcolor: selected ? bg : 'action.hover',
                    },
                  }}
                >
                  {selected && (
                    <Box sx={{
                      position: 'absolute', top: 8, right: 8,
                      width: 20, height: 20, borderRadius: '50%',
                      bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={12} color="#fff" />
                    </Box>
                  )}
                  <Icon size={22} style={{ color, marginBottom: 8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: selected ? color : 'text.primary' }}>
                    {name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleCreateMasterPO}
          disabled={isCreating || !poName || selectedPlatforms.length === 0}
          fullWidth
          sx={{
            bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' },
            py: 1.5, fontSize: '0.875rem', fontWeight: 600,
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          {isCreating ? 'Creating...' : `Create Purchase Order${selectedPlatforms.length > 0 ? ` (${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''})` : ''}`}
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateMasterPO;
